import uuidv1 from 'uuid/v1';
import immutable from 'object-path-immutable';
import { get } from 'lodash';

import {
  createNewSpace,
  getUpdatedTableColumnById,
  getTableColumnKey,
  validateAllSpaceWithTableData,
  getSpaceFieldIdsByDepth,
  postMessage,
  safeNumber,
} from './helper';
import { getAppConfigData, updateAppConfig } from './network';
import { IState } from './types';
import { ElementTypes } from '../../types/element';
import { ISpace, OrderChangeType, IShadow } from '../../types/space';
import { getAllConflictResolvedShadowClones, getSpacesToKageNoBunshinNoJitsu } from './shadow';
import { IDispatch, IRootState } from '../stores';
import { PostMessageEvent } from '../../types/message';
import { IViewResponse } from '../core/types';
import { isDisabledOrDeletedSyncTable } from '../core/helpers';
import { getForeignTableIdByType } from '../../helpers';

const MAX_SPACE_LIMIT = safeNumber(get(process.env, 'REACT_APP_MAX_SPACE_LIMIT', undefined), 100);

const getInitialState = (): IState => ({
  // Is loading
  isLoading: false,
  // Loading printing
  isLoadingPrinting: false,
  isLoadingTableData: true,
  // Controls editing mode or not.
  isEditingMode: false,
  // Controls the print functionality, if set to true will trigger the print
  isPrinting: false,
  // Controlling which mode. Portrait or landscape
  isPortrait: true,
  hasChanged: false,
  // Space ID of editing space in the sidebar
  editingSpaceId: null,
  // *For record ref only* The depth of record ref. Does not include 1st. We use index because tableColumns is an array
  indexDepth: [],
  // Attachment field id being edited
  editingAttachment: null,
  // Space by ID. ID is generated with uuid.
  byId: {},
  // A copy of each space, saving their shadow position and size.
  shadowById: {},
  // String if space Ids. The ordering here is used as z-index placement.
  allIds: [],
});

const model = {
  state: getInitialState(),
  reducers: {
    setIsLoading(state: IState, payload: boolean) {
      return { ...state, isLoading: payload };
    },
    setLoadingTableData(state: IState, payload: boolean) {
      return { ...state, isLoadingTableData: payload };
    },
    setRetrievedSpaces(state: IState, payload: any) {
      return { ...state, ...payload };
    },
    setHasChanged(state: IState, payload: boolean) {
      return { ...state, hasChanged: payload };
    },
    addSpace(state: IState, payload: ISpace<ElementTypes>) {
      return {
        ...state,
        hasChanged: true,
        byId: immutable.set(state.byId, `${payload.id}`, payload),
        allIds: immutable.push(state.allIds, '', payload.id),
        editingSpaceId: payload.id,
        indexDepth: [],
      };
    },
    updatePortraitMode(state: IState, isPortrait: boolean) {
      return { ...state, hasChanged: true, isPortrait };
    },
    updateSpace(state: IState, payload: any) {
      return immutable(state)
        .assign(`byId.${payload.id}`, payload.updates)
        .set('hasChanged', true)
        .value();
    },
    updateSpaceConfig(state: IState, payload: any) {
      // Record ref could have 'headerStyle' or 'rowStyle' for propKey
      const path = payload.propKey
        ? `byId.${payload.id}.${payload.propKey}`
        : `byId.${payload.id}.config`;
      return immutable(state).assign(path, payload.updates).set('hasChanged', true).value();
    },
    updateSpaceOrder(
      state: IState,
      { spaceId, change }: { spaceId: string; change: OrderChangeType },
    ) {
      const spaceIdx = state.allIds.indexOf(spaceId);
      if (spaceIdx === -1) return state;

      const allIdsWithoutSpace = immutable.del(state.allIds, String(spaceIdx));
      const TARGET_INDEX_MAP = {
        [OrderChangeType.OneBack]: spaceIdx - 1,
        [OrderChangeType.OneForward]: spaceIdx + 1,
        [OrderChangeType.AllBack]: 0,
        [OrderChangeType.AllForward]: allIdsWithoutSpace.length,
      };
      const reordered = immutable.insert(allIdsWithoutSpace, '', spaceId, TARGET_INDEX_MAP[change]);
      return immutable(state).set('allIds', reordered).set('hasChanged', true).value();
    },
    removeSpace(state: IState) {
      if (!state.editingSpaceId) return state;

      const spaceIdx = state.allIds.indexOf(state.editingSpaceId);
      if (spaceIdx === undefined) return state;

      return {
        ...state,
        hasChanged: true,
        editingSpaceId: null,
        byId: immutable.del(state.byId, `${state.editingSpaceId}`),
        allIds: immutable.del(state.allIds, String(spaceIdx)),
      };
    },
    dupelicateSpaceById(state: IState, spaceId: string) {
      const id = uuidv1();
      return {
        ...state,
        hasChanged: true,
        byId: immutable.set(state.byId, id, {
          ...state.byId[spaceId],
          id,
          posX: state.byId[spaceId].posX + 10,
          posY: state.byId[spaceId].posY + 10,
        }),
        allIds: immutable.push(state.allIds, '', String(id)),
      };
    },
    setEditingSpaceId(state: IState, payload: string) {
      return {
        ...state,
        editingSpaceId: payload,
        editingAttachment: null,
        indexDepth: [],
      };
    },
    setShadowClones(state: IState, payload: any) {
      return { ...state, shadowById: payload };
    },
    removeEditingSpace(state: IState) {
      return immutable.set(state, `editingSpaceId`, null);
    },
    toggleEditingMode(state: IState) {
      return { ...state, isEditingMode: !state.isEditingMode, editingSpaceId: null };
    },
    togglePrinting(state: IState) {
      return { ...state, isPrinting: !state.isPrinting };
    },

    /** Special Reference Spaces */
    addTableColumn(state: IState, { newTableCol, spaceId }: any) {
      const key = `${getTableColumnKey(state.indexDepth)}.tableColumns`;
      return immutable(state)
        .push(`byId.${spaceId}.${key}`, newTableCol)
        .set('hasChanged', true)
        .value();
    },
    updateTableColumnName(state: IState, { spaceId, colIdIndex, name }: any) {
      // This one is needed because we need to specifically pass in a colIdIndex
      const key = `${getTableColumnKey(state.indexDepth)}.tableColumns`;
      return immutable(state)
        .set(`byId.${spaceId}.${key}.${colIdIndex}.displayName`, name)
        .set('hasChanged', true)
        .value();
    },
    updateTableColumnEntirety(
      state: IState,
      { newColumns, spaceId }: { newColumns: any[]; spaceId: string },
    ) {
      const key = `${getTableColumnKey(state.indexDepth)}.tableColumns`;
      return immutable(state)
        .set(`byId.${spaceId}.${key}`, newColumns)
        .set('hasChanged', true)
        .value();
    },
    removeTableColumn(state: IState, { spaceId, colIdIndex }: any) {
      const key = `${getTableColumnKey(state.indexDepth)}.tableColumns`;
      return immutable(state)
        .del(`byId.${spaceId}.${key}.${colIdIndex}`)
        .set('hasChanged', true)
        .value();
    },
    addIndexDepth(state: IState, indexDepth: any) {
      return immutable.push(state, `indexDepth`, indexDepth);
    },
    removeIndexDepth(state: IState) {
      return immutable.del(state, `indexDepth.${state.indexDepth.length - 1}`);
    },
    addEditingAttachment(state: IState, editingAttachment: IState['editingAttachment']) {
      return immutable.set(state, 'editingAttachment', editingAttachment);
    },
    removeEditingAttachment(state: IState) {
      return immutable.set(state, 'editingAttachment', null);
    },
    setLoadPrinting(state: IState, isLoading: boolean) {
      return { ...state, isLoadingPrinting: isLoading };
    },
  },
  effects: (dispatch: IDispatch) => ({
    async getSpaceConfigs(
      { isRefresh, tableId }: { isRefresh?: boolean; tableId: string },
      { core }: IRootState,
    ) {
      if (!isRefresh) dispatch.space.setIsLoading(true);
      if (!core.currentBlockId) return;

      try {
        const results = await getAppConfigData(core.currentBlockId);
        const config = get(results, 'getAppConfig.body.config');

        if (config) {
          const parsedConfig = JSON.parse(config);

          const [resultsAllIds, resultsById] = validateAllSpaceWithTableData({
            spacesById: parsedConfig.byId,
            spacesAllIds: parsedConfig.allIds,
            // If the lastTableId is different, the table source must have just changed. On
            // table source change, all non-static spaces are to be removed. This is toggled
            // by enabling the suppressInvalidSpaces boolean.
            suppressInvalidSpaces: parsedConfig.lastTableId !== tableId,
          });

          dispatch.space.setRetrievedSpaces({
            isLoading: false,
            byId: resultsById || {},
            allIds: resultsAllIds || [],
            isPortrait: parsedConfig && parsedConfig.isPortrait === false ? false : true,
          });
        } else {
          dispatch.space.setRetrievedSpaces({
            isLoading: false,
            byId: {},
            allIds: [],
            isPortrait: true,
          });
        }

        dispatch.space.unfoldShadowClones();
      } catch (err) {
        console.warn('Error in getSpaceConfigsByTemplate: ', err);
        dispatch.core.setError({ err: err });
      }
    },
    // after space configs are built, check to see if any foreign tables need to be pulled to populate space with data
    async getInitialTablesFromSpaces(
      { accum }: { accum: IViewResponse; isRefresh?: boolean },
      { core }: IRootState,
    ) {
      const fieldsByDepth: { [depth: number]: string[] } = {};
      const visibleNodes = core.visibleNodes;
      let newAccum = { ...accum };

      // get the fields needed to populate all visible spaces
      getSpaceFieldIdsByDepth({
        depth: 1,
        depthLimit: 3,
        fieldsByDepth,
      });

      try {
        // for each depth of fields, request any missing data from back-end
        for (const depth in fieldsByDepth) {
          const tableIds: string[] = [];
          const fieldIds = fieldsByDepth[depth];

          for (const fieldId of fieldIds) {
            const field = newAccum.fieldById[fieldId];

            if (field) {
              const tableId = getForeignTableIdByType(field);

              if (
                tableId &&
                !newAccum.tableById[tableId] &&
                !tableIds.includes(tableId) &&
                visibleNodes[tableId] &&
                !isDisabledOrDeletedSyncTable(visibleNodes[tableId].syncTableMeta)
              ) {
                tableIds.push(tableId);
              }
            }
          }

          if (tableIds.length) {
            const tableData = await dispatch.core.getBatchedTablesForSpace({
              tableSet: tableIds,
              accum: newAccum,
            });
            if (tableData) newAccum = tableData;
          }
        }
      } catch (err) {
        console.warn('Error in getInitialTablesFromSpaces: ', err);
        dispatch.core.setError({ err: err });
      }

      return newAccum;
    },
    async saveSpaceConfigsByTemplate(_: undefined, { space, core }: IRootState) {
      if (!core.currentBlockId) return;

      try {
        const blockId = core.currentBlockId;
        const config = JSON.stringify({
          byId: space.byId,
          allIds: space.allIds,
          isPortrait: space.isPortrait,
          lastTableId: core.currentTableId,
        });

        postMessage({
          eventName: PostMessageEvent.UPDATE_EXPANDED_CONFIG,
          data: {
            config,
          },
        });

        await updateAppConfig({ blockId, config });
        dispatch.space.setHasChanged(false);

        postMessage({
          eventName: PostMessageEvent.SYNC_BLOCK_APP_CONFIG,
          data: {
            blockId,
          },
        });
      } catch (err) {
        console.warn('Error in saveSpaceConfigsByTemplate: ', err);
      }
    },
    async addTableColumnByType(
      {
        colIdToAdd,
        spaceId,
        suppressData,
      }: { colIdToAdd: string; spaceId: string; suppressData?: boolean },
      { space, core }: IRootState,
    ) {
      if (!suppressData) {
        const field = core.fieldById[colIdToAdd];
        const tableId = getForeignTableIdByType(field);

        if (tableId) {
          if (!core.tableById[tableId]) {
            const data = await dispatch.core.getTableForSpace(tableId);
            if (data) dispatch.core.setIncomingData(data);
          }
        }
      }

      const newTableCol = getUpdatedTableColumnById(colIdToAdd, space.byId[spaceId]);
      dispatch.space.addTableColumn({ newTableCol, spaceId });
    },
    async createAndAddSpace(
      {
        elementType,
        fieldId,
        position,
      }: {
        elementType: ElementTypes;
        fieldId?: string;
        position?: { posX: number; posY: number };
      },
      { core, space }: IRootState,
    ) {
      if (space.allIds.length >= MAX_SPACE_LIMIT) {
        const toast = {
          messageId: 'sidebar.option.error.spacesLimit',
          isError: true,
        };

        dispatch.core.setToast({ toast });
        return;
      }

      if (fieldId) {
        const field = core.fieldById[fieldId];
        const tableId = getForeignTableIdByType(field);

        if (tableId && !core.tableById[tableId]) {
          const data = await dispatch.core.getTableForSpace(tableId);
          if (data) dispatch.core.setIncomingData(data);
        }
      }

      const newSpace = createNewSpace({ elementType, fieldId, position });
      dispatch.space.addSpace(newSpace);
      return newSpace;
    },
    duplicateSpace(spaceId: string, { space }: IRootState) {
      if (space.allIds.length >= MAX_SPACE_LIMIT) {
        const toast = {
          messageId: 'sidebar.option.error.spacesLimit',
          isError: true,
        };

        dispatch.core.setToast({ toast });
        return;
      }

      dispatch.space.dupelicateSpaceById(spaceId);
    },
    unfoldShadowClones(_: undefined, { space }: { space: IState }) {
      /**
       * This is the mighty function that takes all the spaces, and adjusts all of the record reference
       * tables accordingly to their height. And then resolves all the collisions below.
       *
       * All the collision resolving logic is held in `./shadow.ts`
       */
      // 1. Get the list of spaces we need find if there is avoidingCollision. Also get the initial shadowClones
      const [sortedSpaces, shadowClones] = getSpacesToKageNoBunshinNoJitsu(space.byId);

      // 1a. If there are no avoidingCollision spaces, just reset the clones to origin.
      if (sortedSpaces.length === 0) {
        dispatch.space.setShadowClones(shadowClones);
        return;
      }

      // 2. Loop through each space
      const finalClones = getAllConflictResolvedShadowClones({
        sortedSpaces: sortedSpaces as ISpace<ElementTypes>[],
        shadowClones: shadowClones as { [key: string]: IShadow },
      });

      dispatch.space.setShadowClones(finalClones);
    },
  }),
};

export default model;
